import { MakeApiCall } from '../../../../../apis';
import {
    DEFAULT_SMART_DEFAULTS,
    readSmartDefaults,
    setSmartDefaultsCache,
} from '../../../../../utils/smartDefaults';

export const PRODUCT_DEFAULT_SETTINGS_ENDPOINT = 'product-default-settings';
export const PRODUCT_DEFAULT_SETTINGS_SYNC_ENDPOINT = 'product-default-settings/sync';
export const PRODUCT_DEFAULT_VALUE_COUNTS_ENDPOINT = 'products/default-value-counts';

export const SYNC_SCOPES = {
    UNEDITED_ONLY: 'unedited_only',
    ALL: 'all',
    SELECTED: 'selected',
};

const API_FIELD_MAP = {
    cogsPercentOfPrice: 'cogs_percentage',
    replenishmentDays: 'replenishment_time',
    unsellableReturnsPercent: 'unsellable_returns_percentage',
    taxPaidOnCogsPercent: 'tax_on_cogs',
    taxCollectedOnSalesPercent: 'vat_rate',
    fbmShippingCostPerPackage: 'fbm_shipping_cost_per_package',
    fbmSurchargeShippingAbroad: 'fbm_surcharge_shipping_abroad',
};

const valuesEqual = (left, right) => {
    if (left == null && right == null) return true;
    if (left == null || right == null) return false;

    const leftNum = Number(left);
    const rightNum = Number(right);
    if (Number.isFinite(leftNum) && Number.isFinite(rightNum)) {
        return leftNum === rightNum;
    }

    return left === right;
};

/** Build a PUT payload containing only fields that changed. */
export const mapSmartDefaultsChangesToApi = (previousDefaults, nextDefaults) => {
    const payload = {};

    Object.entries(API_FIELD_MAP).forEach(([internalKey, apiKey]) => {
        const previousValue = previousDefaults?.[internalKey];
        const nextValue = nextDefaults?.[internalKey];

        if (!valuesEqual(previousValue, nextValue)) {
            payload[apiKey] = nextValue ?? null;
        }
    });

    return payload;
};

/** Map API payload → internal smart-defaults shape used by the UI. */
export const mapApiToSmartDefaults = (data) => {
    if (!data || typeof data !== 'object') {
        return { ...DEFAULT_SMART_DEFAULTS };
    }

    return {
        cogsPercentOfPrice: data.cogs_percentage ?? DEFAULT_SMART_DEFAULTS.cogsPercentOfPrice,
        replenishmentDays: data.replenishment_time ?? DEFAULT_SMART_DEFAULTS.replenishmentDays,
        unsellableReturnsPercent:
            data.unsellable_returns_percentage ?? DEFAULT_SMART_DEFAULTS.unsellableReturnsPercent,
        taxPaidOnCogsPercent: data.tax_on_cogs ?? DEFAULT_SMART_DEFAULTS.taxPaidOnCogsPercent,
        taxCollectedOnSalesPercent: data.vat_rate ?? DEFAULT_SMART_DEFAULTS.taxCollectedOnSalesPercent,
        fbmShippingCostPerPackage: data.fbm_shipping_cost_per_package ?? null,
        fbmSurchargeShippingAbroad: data.fbm_surcharge_shipping_abroad ?? null,
        updatedAt: data.updated_at ?? null,
    };
};

let inflightRequest = null;
let inflightToken = null;

const buildSaveSuccessMessage = ({ overwriteAll, syncData, settingsChanged, syncMessage } = {}) => {
    const updatedCount = syncData?.updated_count;

    if (settingsChanged === false) {
        return syncMessage || 'Product defaults synced successfully.';
    }

    if (typeof updatedCount === 'number' && updatedCount === 0) {
        return 'Defaults saved. No products needed updating.';
    }

    if (overwriteAll) {
        return typeof updatedCount === 'number'
            ? `Defaults updated and applied to ${updatedCount} products.`
            : 'Defaults updated and applied to all products.';
    }

    return typeof updatedCount === 'number'
        ? `Defaults saved and applied to ${updatedCount} products still on auto.`
        : 'Defaults saved for products still on auto.';
};

/**
 * Fetch product default settings from the API.
 * Deduplicates concurrent requests for the same token.
 */
export const fetchProductDefaultSettings = async (token, { force = false } = {}) => {
    if (!token) {
        return {
            success: false,
            defaults: readSmartDefaults(),
            message: 'Not authenticated',
        };
    }

    if (!force && inflightRequest && inflightToken === token) {
        return inflightRequest;
    }

    inflightToken = token;
    inflightRequest = (async () => {
        const response = await MakeApiCall(
            PRODUCT_DEFAULT_SETTINGS_ENDPOINT,
            'get',
            null,
            true,
            {},
            token
        );

        if (response?.status && response?.data) {
            const defaults = mapApiToSmartDefaults(response.data);
            setSmartDefaultsCache(defaults);
            return { success: true, defaults, raw: response.data };
        }

        return {
            success: false,
            defaults: readSmartDefaults(),
            message: response?.message || 'Unable to load product default settings.',
        };
    })();

    try {
        return await inflightRequest;
    } finally {
        inflightRequest = null;
        inflightToken = null;
    }
};

export const saveProductDefaultSettings = async (token, previousDefaults, nextDefaults) => {
    if (!token) {
        return { success: false, message: 'Not authenticated' };
    }

    const payload = mapSmartDefaultsChangesToApi(previousDefaults, nextDefaults);
    if (Object.keys(payload).length === 0) {
        return {
            success: true,
            defaults: { ...nextDefaults },
            unchanged: true,
        };
    }

    const response = await MakeApiCall(
        PRODUCT_DEFAULT_SETTINGS_ENDPOINT,
        'put',
        payload,
        true,
        {},
        token
    );

    if (response?.status) {
        const defaults = response?.data
            ? mapApiToSmartDefaults(response.data)
            : { ...previousDefaults, ...nextDefaults };

        setSmartDefaultsCache(defaults);
        return { success: true, defaults, raw: response.data };
    }

    return {
        success: false,
        message: response?.message || 'Failed to save product default settings.',
    };
};

export const syncProductDefaultSettings = async (
    token,
    { scope = SYNC_SCOPES.UNEDITED_ONLY, productIds, fields, marketplaceId } = {}
) => {
    if (!token) {
        return { success: false, message: 'Not authenticated' };
    }

    const payload = { scope };

    if (scope === SYNC_SCOPES.SELECTED && Array.isArray(productIds) && productIds.length > 0) {
        payload.product_ids = productIds;
    }

    if (marketplaceId) {
        payload.marketplace_id = marketplaceId;
    }

    if (Array.isArray(fields) && fields.length > 0) {
        payload.fields = fields;
    }

    const response = await MakeApiCall(
        PRODUCT_DEFAULT_SETTINGS_SYNC_ENDPOINT,
        'post',
        payload,
        true,
        {},
        token
    );

    if (response?.status) {
        if (response?.data?.defaults_used) {
            const defaults = mapApiToSmartDefaults(response.data.defaults_used);
            setSmartDefaultsCache(defaults);
        }

        return {
            success: true,
            data: response.data,
            message: response.message,
        };
    }

    return {
        success: false,
        message: response?.message || 'Failed to sync product defaults.',
    };
};

export const saveAndSyncProductDefaultSettings = async (
    token,
    previousDefaults,
    nextDefaults,
    { overwriteAll = false } = {}
) => {
    const saveResult = await saveProductDefaultSettings(token, previousDefaults, nextDefaults);
    if (!saveResult.success) {
        return saveResult;
    }

    const syncResult = await syncProductDefaultSettings(token, {
        scope: overwriteAll ? SYNC_SCOPES.ALL : SYNC_SCOPES.UNEDITED_ONLY,
    });

    if (!syncResult.success) {
        return {
            success: false,
            message: syncResult.message || 'Defaults saved but product sync failed.',
            defaults: saveResult.defaults,
            saved: true,
            synced: false,
        };
    }

    const defaults = syncResult.data?.defaults_used
        ? mapApiToSmartDefaults(syncResult.data.defaults_used)
        : saveResult.defaults;

    setSmartDefaultsCache(defaults);

    return {
        success: true,
        defaults,
        saved: !saveResult.unchanged,
        synced: true,
        syncData: syncResult.data,
        message: buildSaveSuccessMessage({
            overwriteAll,
            syncData: syncResult.data,
            settingsChanged: !saveResult.unchanged,
            syncMessage: syncResult.message,
        }),
    };
};

export const resolveEditedCount = (data) => {
    if (!data || typeof data !== 'object') {
        return 0;
    }

    const hasFilters = Array.isArray(data.filters_applied) && data.filters_applied.length > 0;
    const bucket = hasFilters ? data.filtered : data.overall;
    return Number(bucket?.edited_count) || 0;
};

export const fetchDefaultValueCounts = async (token) => {
    if (!token) {
        return { success: false, editedCount: 0, message: 'Not authenticated' };
    }

    const response = await MakeApiCall(
        PRODUCT_DEFAULT_VALUE_COUNTS_ENDPOINT,
        'get',
        null,
        true,
        {},
        token
    );

    if (response?.status && response?.data) {
        return {
            success: true,
            data: response.data,
            editedCount: resolveEditedCount(response.data),
        };
    }

    return {
        success: false,
        editedCount: 0,
        message: response?.message || 'Unable to load default value counts.',
    };
};
